// ============================================================================
// The Subcontractors hub's one read (item #683).
//
// One record per sub for life (TCSD-0368): the pipeline is the sub's status, the
// bench is v_bench_ready, "nightly or projects" is v_subcontractor_work (the one
// home of that answer — read it, never re-derive it here), the at-risk rule is
// the tier screen's own atRisk() (TCSD-0235, unchanged), and "my markets" are
// the markets of the buildings the person manages — the same buildings My
// Accounts scopes to.
//
// ⛔ Field-service subs only. The same table holds office vendors and staffing
// firms (RSM, Vistage, supply houses); they are not subs and never appear here.
//
// Flat fetch, house style (no TanStack Query). Registered with the save path so
// any write that touches these tables refreshes the hub.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { errorMessage } from '../../../shared/errors';
import { useReader } from '../../../shared/save-path';
import { atRisk, lossPhrase, type TierRow } from '../../hr-onboarding/tier-roster-parts';
import { useSubcontractorTiers } from '../../hr-onboarding/hooks/use-subcontractor-tiers';

export interface HubSub {
  provider_id: string;
  name: string;
  status: string;
  phone: string | null;
  email: string | null;
  markets: string[];
  capabilities: string[];
  kind_source: 'stated' | 'records' | 'unknown';
  does_nightly: boolean;
  does_projects: boolean;
  /** Distinct buildings with a running or booked assignment. */
  live_buildings: number;
  /** Managers of those buildings — a sub on my building is mine whatever its markets. */
  building_ams: string[];
  /** Names of those buildings, for search. */
  building_names: string[];
  tier: 1 | 2 | 3 | 4 | null;
  /** Present only when the tier screen's at-risk rule holds. */
  risk_reason: string | null;
  on_bench: boolean;
  created_at: string;
}

export interface NoSubBuilding {
  facility_id: string;
  facility_name: string;
  market: string | null;
  status: string;
}

export type HubScope = 'mine' | 'all';

const TABLES = ['service_providers', 'service_assignments', 'sourcing_prospects', 'partner_onboarding'] as const;

export function useHubData() {
  const { employee } = useAuth();
  const me = employee?.employee_id ?? null;
  const tiers = useSubcontractorTiers();
  const [subs, setSubs] = useState<HubSub[]>([]);
  const [myMarkets, setMyMarkets] = useState<string[]>([]);
  const [noSub, setNoSub] = useState<NoSubBuilding[]>([]);
  const [facilityMarket, setFacilityMarket] = useState<Map<string, string | null>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);
  useReader(TABLES, refetch);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setError(null);
      try {
        const [sp, work, bench, asg, fac] = await Promise.all([
          supabase
            .from('service_providers')
            .select('provider_id, partner_name, provider_status, phone, email, coverage_markets, capabilities, created_at')
            .eq('provider_class', 'Field_Service')
            .order('partner_name'),
          supabase.from('v_subcontractor_work').select('provider_id, kind_source, does_nightly, does_projects'),
          supabase.from('v_bench_ready').select('provider_id'),
          supabase
            .from('service_assignments')
            .select('provider_id, facility_id, assignment_status')
            .in('assignment_status', ['Active', 'Scheduled']),
          supabase
            .from('facilities')
            .select('facility_id, facility_name, status, market_region, account_manager_eid')
            .in('status', ['Active', 'Onboarding', 'Pending']),
        ]);
        for (const r of [sp, work, bench, asg, fac]) if (r.error) throw r.error;
        if (cancelled) return;

        type Fac = { facility_id: string; facility_name: string; status: string; market_region: string | null; account_manager_eid: string | null };
        const facs = (fac.data ?? []) as Fac[];
        const facById = new Map(facs.map((f) => [f.facility_id, f]));
        setFacilityMarket(new Map(facs.map((f) => [f.facility_id, f.market_region])));

        // My markets = the markets of the buildings I manage (My Accounts' own set).
        const mine = new Set<string>();
        if (me) for (const f of facs) if (f.account_manager_eid === me && f.status === 'Active' && f.market_region) mine.add(f.market_region);
        setMyMarkets([...mine].sort());

        const workBy = new Map(
          ((work.data ?? []) as Array<{ provider_id: string; kind_source: HubSub['kind_source']; does_nightly: boolean; does_projects: boolean }>).map((w) => [w.provider_id, w]),
        );
        const benchIds = new Set(((bench.data ?? []) as Array<{ provider_id: string }>).map((b) => b.provider_id));
        const liveBy = new Map<string, Set<string>>();
        const covered = new Set<string>();
        for (const a of (asg.data ?? []) as Array<{ provider_id: string | null; facility_id: string }>) {
          if (!a.provider_id) continue;
          covered.add(a.facility_id);
          let s = liveBy.get(a.provider_id);
          if (!s) liveBy.set(a.provider_id, (s = new Set()));
          s.add(a.facility_id);
        }
        setNoSub(
          facs
            .filter((f) => !covered.has(f.facility_id))
            .map((f) => ({ facility_id: f.facility_id, facility_name: f.facility_name, market: f.market_region, status: f.status })),
        );

        type Sp = { provider_id: string; partner_name: string | null; provider_status: string; phone: string | null; email: string | null; coverage_markets: string[] | null; capabilities: string[] | null; created_at: string };
        setSubs(
          ((sp.data ?? []) as Sp[]).map((r) => {
            const w = workBy.get(r.provider_id);
            const live = liveBy.get(r.provider_id) ?? new Set<string>();
            return {
              provider_id: r.provider_id,
              name: r.partner_name ?? r.provider_id,
              status: r.provider_status,
              phone: r.phone,
              email: r.email,
              markets: r.coverage_markets ?? [],
              capabilities: r.capabilities ?? [],
              kind_source: w?.kind_source ?? 'unknown',
              does_nightly: Boolean(w?.does_nightly),
              does_projects: Boolean(w?.does_projects),
              live_buildings: live.size,
              building_ams: [...new Set([...live].map((id) => facById.get(id)?.account_manager_eid).filter((x): x is string => Boolean(x)))],
              building_names: [...live].map((id) => facById.get(id)?.facility_name).filter((x): x is string => Boolean(x)),
              tier: null,
              risk_reason: null,
              on_bench: benchIds.has(r.provider_id),
              created_at: r.created_at,
            };
          }),
        );
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load the subcontractors.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me, tick]);

  // Tier and the at-risk reason come from the tier screen's own rule (TCSD-0235).
  const merged = useMemo(() => {
    const tierRows = tiers.rows as unknown as TierRow[];
    const tierBy = new Map(tierRows.map((t) => [t.provider_id, t]));
    const risky = new Map(atRisk(tierRows).map((t) => [t.provider_id, t]));
    return subs.map((s) => {
      const t = tierBy.get(s.provider_id);
      const r = risky.get(s.provider_id);
      return {
        ...s,
        tier: t ? t.tier : null,
        risk_reason: r ? riskReason(r) : null,
      };
    });
  }, [subs, tiers.rows]);

  return {
    me,
    subs: merged,
    myMarkets,
    noSub,
    facilityMarket,
    loading: loading || tiers.loading,
    error: error ?? tiers.error,
    refetch: () => {
      refetch();
      tiers.refetch();
    },
  };
}

/** The reason on an at-risk row, in the tier screen's own words. */
export function riskReason(r: TierRow): string {
  if (r.facilities_lost_60d > 0) return `${lossPhrase(r)} in the last 60 days`;
  if (r.days_with_tcs === null) return 'no start date on file';
  return `${r.days_with_tcs} days at TCS`;
}

/** Is this sub in the person's scope? Covers one of my markets, or works one of my buildings. */
export function inScope(s: HubSub, scope: HubScope, myMarkets: string[], me: string | null): boolean {
  if (scope === 'all') return true;
  if (s.markets.some((m) => myMarkets.includes(m))) return true;
  return me !== null && s.building_ams.includes(me);
}

/** Active / Pending / Onboarding with no running or booked building, and not
 *  already answered "projects only" by an account manager. */
export function needsAStatus(s: HubSub): boolean {
  if (!['Active', 'Pending', 'Onboarding'].includes(s.status)) return false;
  if (s.live_buildings > 0) return false;
  const answeredProjectsOnly = s.kind_source === 'stated' && s.does_projects && !s.does_nightly;
  return !answeredProjectsOnly;
}

/** Short market names for a row: "St. Louis · Kansas City". */
export function shortMarkets(markets: string[]): string {
  return markets.map((m) => m.replace(/\s*Metro$/, '')).join(' · ');
}
